<template>
    <div class="certificates-container">
      <div class="certificate-container-header"> 
        <h3>My Certificates</h3>  
      </div>
  
      <div class="certificates-grid">
        <template v-if="isLoading">
          <div v-for="row in 4" :key="'skeleton-' + row" class="certificate-card skeleton-card">
            <div class="ribbon-icon">
              <div class="skeleton-ribbon"></div>
            </div>
            <div class="certificate-content">
              <div class="skeleton-line skeleton-badge"></div>
              <div class="skeleton-line skeleton-title"></div>
              <div class="certificate-details skeleton-details">
                <div class="skeleton-line skeleton-detail"></div>
                <div class="skeleton-line skeleton-detail"></div>
              </div>
              <div class="actions">
                <div class="skeleton-button"></div>
              </div>
            </div>
          </div>
        </template>
  
        <div v-else-if="userCertificates.length === 0" key="no-results" class="no-results">
          <p>No Certificates Found</p>
        </div>
  
        <template v-else>
          <div
            v-for="certificate in userCertificates"
            :key="certificate.userCertificate.id"
            class="certificate-card"
          >
            <div class="ribbon-icon">
              <svg width="60" height="80" viewBox="0 0 60 80" fill="none">
                <path d="M0 0H60V60L30 80L0 60V0Z" fill="var(--primary-color)" />
                <path
                  d="M30 37.5C35.799 37.5 40.5 32.799 40.5 27C40.5 21.201 35.799 16.5 30 16.5C24.201 16.5 19.5 21.201 19.5 27C19.5 32.799 24.201 37.5 30 37.5Z"
                  stroke="white"
                  stroke-width="3"
                />
                <path
                  d="M30 42C23.3726 42 18 47.3726 18 54H42C42 47.3726 36.6274 42 30 42Z"
                  stroke="white"
                  stroke-width="3"
                />
              </svg>
            </div>
            <div class="certificate-content">
              <span class="achievement-text">Certificate of Achievement</span>
              <h2 class="certificate-title">{{ certificate.userCertificate.title }}</h2>
              <div class="certificate-details">
                <span :id="'issue-date-text'+certificate.userCertificate.id">
                  <i class="fas fa-calendar-alt"></i>
                  {{ formatDate(certificate.userCertificate.issueDate) }}
                </span>
                <b-tooltip :target="'issue-date-text'+certificate.userCertificate.id" placement="top"> Issue Date </b-tooltip>
                <span :id="'validity-date-text'+certificate.userCertificate.id" v-if="certificate.userCertificate.validUntil">
                  <i class="fas fa-hourglass-end"></i>
                  {{ formatDate(certificate.userCertificate.validUntil) }}
                </span>
                <b-tooltip :target="'validity-date-text'+certificate.userCertificate.id" placement="top"> Validity Date </b-tooltip>
              </div>
              <div class="actions">
                <button
                  class="action-btn download-btn"
                  @click="downloadCertificate(certificate.userCertificate.certificateURL, certificate.userCertificate.title)"
                >
                  <i class="fas fa-download"></i> Download
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </template>
  
  <script>
  import { mapActions, mapGetters } from "vuex";
  
  export default {
    name: "Certificates",
    data() {
      return {
        isLoading: true
      };
    },
    computed: {
      ...mapGetters("certificate", ["userCertificates"]),
    },
    methods: {
      ...mapActions("certificate", ["fetchCurrentUserCertificates"]),
  
      formatDate(dateString) {
        const options = { year: "numeric", month: "long", day: "numeric" };
        return new Date(dateString).toLocaleDateString(undefined, options);
      },
  
      async downloadCertificate(url, title) {
        try {
          const response = await fetch(url);

          if (!response.ok) { throw new Error(); }

          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `${title || "certificate"}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
          this.$toasted.error("Unable to download certificate. Please try again later.", {
            icon: "exclamation-triangle",
            theme: "outline",
            position: "bottom-center",
            singleton: true,
            duration: 3000
          });
        }
      }
    },
  
    async mounted() {
      try {
        await this.fetchCurrentUserCertificates();
      } catch (error) {
        this.$toasted.error("Unable to load user certificates.", {
          icon: "exclamation-triangle",
          theme: "outline",
          position: "bottom-center",
          singleton: true,
          duration: 3000
        });
      } finally {
        this.isLoading = false;
      }
    }
  };
  </script>
  
  <style scoped>
  .certificates-container {
    margin: 0 auto;
    padding: 2rem;
    font-family: 'Circular Std';
    background-color: #f8f9fa;
    min-height: 100vh;
  }
  
  .certificate-container-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }
  
  .certificate-container-header > h3 {
    border-radius: 2px;
    padding: 2.5rem 0 0 0rem;
    font-weight: 600;
  }
  
  .certificates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
    gap: 1.5rem;
    padding: 1rem 0;
  }
  
  .certificate-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 2rem;
    position: relative;
    transition: all 0.3s ease;
    overflow: hidden;
    cursor: pointer;
  }
  
  .certificate-card:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  .ribbon-icon {
    display: flex;
    justify-content: center;
  }
  
  .certificate-content {
    position: relative;
    z-index: 2;
    margin-top: 2rem;
    text-align: center;
  }
  
  .achievement-text {
    color: #666;
    font-size: 0.7rem;
    display: block;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .certificate-title {
    font-size: 1.1rem;
    color: #333;
    margin: 0.5rem 0;
    line-height: 1.4;
    font-weight: 600;
  }
  
  .certificate-details {
    display: flex;
    justify-content: center;
    gap: 1rem;
    font-size: 0.9rem;
    color: #666;
    margin: 1rem 0;
  }
  
  .certificate-details i {
    margin-right: 0.5rem;
    color: var(--primary-color);
  }
  
  .actions {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }
  
  .action-btn {
    background: none;
    border: 2px solid var(--primary-color);
    color: var(--primary-color);
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .action-btn i {
    margin-right: 0.5rem;
  }
  
  .action-btn:hover {
    background-color: var(--primary-color);
    color: white;
  }
  
  .download-btn {
    background-color: var(--primary-color);
    color: white;
  }
  
  .download-btn:hover {
    background-color: var(--secondary-color);
    border-color: var(--secondary-color);
  }
  
  .no-results {
    text-align: center;
    margin: 4rem 0;
    color: #666;
    grid-column: 1 / -1;
  }
  
  /* Skeleton loader */
  .skeleton-card {
    background: #fff;
    border-radius: 12px;
    padding: 2rem;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    animation: pulse 1.5s infinite;
  }
  
  .skeleton-line {
    height: 1rem;
    border-radius: 8px;
    background: #e0e0e0;
    margin: 0.5rem auto;
  }
  
  .skeleton-badge {
    width: 40%;
    height: 0.75rem;
  }
  
  .skeleton-title {
    width: 70%;
    height: 1.25rem;
  }
  
  .skeleton-detail {
    width: 60%;
    height: 0.875rem;
  }
  
  .skeleton-button {
    width: 120px;
    height: 2.5rem;
    background: #e0e0e0;
    border-radius: 50px;
    margin: 0 auto;
  }
  
  .skeleton-ribbon {
    width: 60px;
    height: 80px;
    background: #e0e0e0;
    border-radius: 4px;
  }
  
  @keyframes pulse {
    0% { background-color: #eeeeee; }
    50% { background-color: #dddddd; }
    100% { background-color: #eeeeee; }
  }
  
  @media (max-width: 768px) {
    .certificates-container {
      padding: 1rem;
    }
  
    .certificates-grid {
      grid-template-columns: 1fr;
    }
  
    .certificate-card:hover {
      transform: none;
    }
  
    .certificate-details {
      flex-direction: column;
      align-items: center;
    }
  
    .actions {
      flex-direction: column;
    }
  
    .action-btn {
      width: 100%;
    }
  }
  </style>
  